import React, { useRef, useEffect } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Ico } from '../ui';
import { getPageIcon } from '../../config/pageTitles';
// Этот импорт грузит стор задач на любом роуте — им же восстанавливается поллинг после F5.
import { useActiveJobCount } from '../../hooks/useAiJobs';

// Панель вкладок над контентом (стиль Notion/VS Code). sticky внутри <main>,
// поэтому ширина сайдбара учитывается автоматически.
const TabBar = ({ tabs, activePath, onActivate, onClose }) => {
  const scrollerRef = useRef(null);
  const activeChipRef = useRef(null);
  const activeJobs = useActiveJobCount();

  // Активная вкладка всегда в видимой области: если чип (целиком) не виден — центрируем.
  // scrollLeft напрямую, без scrollIntoView: тот в sticky-панели ненадёжен и дёргает окно.
  // tabs.length в зависимостях — список восстанавливается из localStorage уже после маунта.
  useEffect(() => {
    const sc = scrollerRef.current;
    const chip = activeChipRef.current;
    if (!sc || !chip) return;
    const left = chip.getBoundingClientRect().left - sc.getBoundingClientRect().left + sc.scrollLeft;
    const right = left + chip.offsetWidth;
    if (left < sc.scrollLeft || right > sc.scrollLeft + sc.clientWidth) {
      sc.scrollTo({ left: left - (sc.clientWidth - chip.offsetWidth) / 2 });
    }
  }, [activePath, tabs.length]);

  // Вертикальное колесо — в горизонтальную прокрутку панели
  const handleWheel = (e) => {
    if (scrollerRef.current && e.deltaY) scrollerRef.current.scrollLeft += e.deltaY;
  };

  return (
    <Box
      ref={scrollerRef}
      onWheel={handleWheel}
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1100,
        display: 'flex',
        alignItems: 'stretch',
        height: 38,
        minHeight: 38,
        bgcolor: 'var(--surface-sidebar)',
        borderBottom: '1px solid var(--color-neutral-300)',
        overflowX: 'auto',
        overflowY: 'hidden',
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
        '@media print': { display: 'none !important' },
      }}
    >
      {tabs.map((tab) => {
        const active = tab.path === activePath;
        return (
          <Box
            key={tab.path}
            ref={active ? activeChipRef : null}
            onClick={() => onActivate(tab)}
            onMouseDown={(e) => { if (e.button === 1) e.preventDefault(); }}
            onAuxClick={(e) => { if (e.button === 1) onClose(tab.path); }}
            title={tab.title}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.25,
              maxWidth: 200,
              flex: '0 0 auto',
              cursor: 'pointer',
              userSelect: 'none',
              borderRight: '1px solid var(--color-neutral-300)',
              bgcolor: active ? 'var(--color-neutral-100)' : 'transparent',
              boxShadow: active ? 'inset 0 2px 0 var(--color-accent-500), 0 0 0 1px var(--color-neutral-300)' : 'none',
              '&:hover': { bgcolor: active ? 'var(--color-neutral-100)' : 'rgba(245, 244, 255, 0.7)' }, // accent-100 70%
              '&:hover .tab-close': { opacity: 1 },
            }}
          >
            <i
              className={`ph ${getPageIcon(tab.path)}`}
              style={{ fontSize: 14, flexShrink: 0, color: active ? 'var(--color-accent-800)' : 'var(--color-neutral-500)' }}
            />
            <Typography
              noWrap
              sx={{ fontSize: 13, fontWeight: active ? 600 : 400, color: active ? 'var(--color-neutral-900)' : 'var(--color-neutral-600)' }}
            >
              {tab.title}
            </Typography>
            {tab.path.startsWith('/ai-photo') && activeJobs > 0 && (
              <>
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    flexShrink: 0,
                    borderRadius: '50%',
                    border: '2px solid var(--color-neutral-300)',
                    borderTopColor: 'var(--color-accent-500)',
                    animation: 'spin 1s linear infinite',
                    '@keyframes spin': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' },
                    },
                  }}
                />
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: 'var(--color-accent-800)' }}>
                  {activeJobs}
                </Typography>
              </>
            )}
            <IconButton
              className="tab-close"
              size="small"
              aria-label="Закрыть вкладку"
              onClick={(e) => { e.stopPropagation(); onClose(tab.path); }}
              sx={{
                p: 0.25,
                flexShrink: 0,
                opacity: active ? 0.6 : 0,
                color: '#73726e',
                '&:hover': { opacity: 1, bgcolor: 'rgba(0,0,0,0.08)' },
              }}
            >
              <Ico name="ph-x" size={14} style={{ verticalAlign: 'baseline', lineHeight: 1 }} />
            </IconButton>
          </Box>
        );
      })}
    </Box>
  );
};

export default TabBar;
